import PropTypes from 'prop-types';

import marketplace from 'cfg/marketplace.json';
import ContentBox from 'components/checkout/caap/ContentBox';
import { isDigitalDeliveryOnlyCart } from 'helpers/CheckoutUtils';
import { toUSD } from 'helpers/NumberFormats';
import { row as styleRow } from 'styles/components/checkout/caap/layout.scss';
import styles from 'styles/components/checkout/caap/spc/chargeSummary.scss';

const { checkout: { showShippingDiscountLineItem } } = marketplace;

const ChargeSummary = ({ cartType, chargeSummary, className, numItems, purchaseGiftCard = {} }, { testId }) => {
  const {
    couponTotal,
    estimatedTax,
    giftWrapCharge,
    grandTotal,
    shippingCharge,
    shippingDiscount,
    shippingSubtotal,
    subTotal,
    total,
    totalBeforeTax
  } = chargeSummary;

  const shippingParams = { shippingDiscount, shippingSubtotal, shippingCharge };
  const displayedShippingCharge = getShippingCharge(shippingParams);
  const displayedShippingDiscount = getShippingLineItemDiscount(shippingParams);

  const { amountRemaining = 0 } = purchaseGiftCard;
  const isDigitalDeliveryOnly = isDigitalDeliveryOnlyCart(cartType);

  // yay, a table!
  return (
    <ContentBox
      isSolo={isDigitalDeliveryOnly}
      isLast={!isDigitalDeliveryOnly}
      isNonRadio={true}>
      <div className={`${styleRow} ${className ? className : ''}`}>
        <table className={styles.chargeSummary}>
          <tbody>
            <tr>
              <td data-test-id={testId('itemCount')}>{ numItems > 1 ? `Items (${numItems})` : 'Items' }:</td>
              <td data-test-id={testId('itemSubTotal')}>{toUSD(subTotal)}</td>
            </tr>

            { giftWrapCharge
              ? <tr>
                <td>Gift Wrap:</td>
                <td data-test-id={testId('giftWrapTotal')}>{toUSD(giftWrapCharge)}</td>
              </tr>
              : null
            }

            <tr>
              <td>
                Shipping:
              </td>
              <td data-test-id={testId('shippingCharge')}>
                { displayedShippingCharge }
              </td>
            </tr>

            { displayedShippingDiscount
              ? <tr>
                <td>Free Shipping:</td>
                <td data-test-id={testId('shippingDiscount')}>-{displayedShippingDiscount}</td>
              </tr>
              : null
            }

            { couponTotal
              ? <tr>
                <td>Discounts:</td>
                <td data-test-id={testId('couponDiscount')}>-{toUSD(couponTotal)}</td>
              </tr>
              : null
            }

            <tr>
              <td>Total before tax:</td>
              <td data-test-id={testId('preTaxTotal')} className={styles.totalBeforeTax}>{toUSD(totalBeforeTax)}</td>
            </tr>

            <tr>
              <td>
                Estimated tax to be collected:*
              </td>
              <td data-test-id={testId('estimatedTax')}>{toUSD(estimatedTax)}</td>
            </tr>

            { total && amountRemaining
              ? <tr>
                <td>
                    Total:
                </td>
                <td data-test-id={testId('totalAmountRemaining')}>{toUSD(total)}</td>
              </tr>
              : null
            }

            { amountRemaining
              ? <tr>
                <td>Gift Cards:</td>
                <td data-test-id={testId('gcRemaining')}>-{toUSD(amountRemaining)}</td>
              </tr>
              : null
            }

            <tr className={styles.orderTotalRow}>
              <td>
                Order total:
              </td>
              <td data-test-id={testId('grandTotal')}>
                {toUSD(grandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentBox>
  );
};

export function getShippingCharge({ shippingSubtotal, shippingCharge }) {
  if (showShippingDiscountLineItem) {
    return toUSD(shippingSubtotal);
  } else {
    return shippingCharge === 0
      ? 'Free'
      : toUSD(shippingCharge);
  }
}

export function getShippingLineItemDiscount({ shippingDiscount }) {
  if (showShippingDiscountLineItem && shippingDiscount) {
    return toUSD(shippingDiscount);
  }
}

ChargeSummary.contextTypes = {
  testId: PropTypes.func
};

export default ChargeSummary;
